import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const UrgencyProofSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const proofPoints = [
    {
      stat: "20%",
      text: "Knowledge workers spend roughly 20% of their time searching for information.",
      source: "McKinsey",
    },
    {
      stat: "40%",
      text: "Executives spend nearly 40% of their time making decisions, and much of that time is poorly used.",
      source: "McKinsey",
    },
    {
      stat: "$12.9M",
      text: "Poor data quality costs organizations at least $12.9 million per year on average.",
      source: "Gartner",
    },
    {
      stat: "53.8%",
      text: "53.8% of organizations measure knowledge investments by reduced time to problem resolution.",
      source: "IDC",
    },
    {
      stat: "38.2%",
      text: "38.2% of organizations track reduced loss of business-critical knowledge as a value metric.",
      source: "IDC",
    },
  ];

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative bg-[#0A0A0B]"
      data-testid="urgency-proof-section"
    >
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="03" name="Context Engine" />
          <span className="mono-label">The Urgency</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            Why AI needs better <span className="gradient-text">enterprise context</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg">
            The urgency is not just AI adoption. It is the cost of fragmented information, 
            slow decision-making, weak data quality, and poor continuity across the enterprise.
          </p>
        </motion.div>

        {/* Proof Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {proofPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`p-6 rounded-xl bg-[#0F0F10] border border-white/5 hover:border-[#00E5FF]/20 transition-colors ${
                index === proofPoints.length - 1 && proofPoints.length % 3 !== 0 
                  ? "md:col-span-2 lg:col-span-1" 
                  : ""
              }`}
              data-testid={`proof-card-${index + 1}`}
            >
              <div className="text-3xl md:text-4xl font-bold text-[#00E5FF] font-['Outfit'] mb-4">
                {point.stat}
              </div>
              <p className="text-[#FAFAFA] text-sm md:text-base leading-relaxed mb-4">
                {point.text}
              </p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider">
                Source: {point.source}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UrgencyProofSection;
